import { Button } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { Dispatch, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../components/spinner";
import { cancelReservation } from "../../../api";
import {
  ApiException,
  MimeRejection,
  SkpEnvironment,
  TransferDraft,
  TransferDraftErrors,
} from "../../../entities";
import { useSkpEnvironment } from "../../../providers/environment";
import { UploadDialogThemeProvider } from "../../../providers/muiThemeProvider/uploadDialogThemeProvider";
import {
  useTransferDraft,
  useTransferDraftDispatch,
} from "../../../providers/transferDraft";
import {
  TransferDraftAction,
  TransferDraftActionType,
} from "../../../reducers/transferDraft";
import uploadMailboxTransfer, {
  MimeTypeRejectedError,
  UploadAbortedError,
} from "../../../services/transferUpload";
import type { ResumeContext } from "../../../services/transferUpload";
import { validateFormAndReturnErrorIfAny } from "../../../utils/formValidator";
import { isApiException, getErrorMessage } from "../../../utils/typeGuard";
import UploadDialog from "./uploadDialog";

import ExceptionDialog from "../../../components/errorDialog";
import useWakeLock from "../../../hooks/useWakelock";
import { useWindowDimensions } from "../../../hooks/useWindowDimensions";
import {
  UploadStatus,
  UploadStatusProvider,
} from "../../../providers/uploadStatus";

export enum TransferAbortionState {
  none = "none",
  aborting = "aborting",
  aborted = "aborted",
}

type SendButtonProps = {
  validateDynamicForm?: () => Promise<boolean>;
  onValidationFailed?: () => void;
};

export default function SendButton({
  validateDynamicForm,
  onValidationFailed,
}: SendButtonProps) {
  const { t } = useTranslation();
  const { vanityLink } = useParams();
  const environment: SkpEnvironment = useSkpEnvironment()!;
  const transferDraft: TransferDraft = useTransferDraft();
  const transferDraftDispatch: Dispatch<TransferDraftAction> =
    useTransferDraftDispatch();

  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [abortState, setAbortState] = useState<TransferAbortionState>(
    TransferAbortionState.none
  );

  const [mimeRejection, setMimeRejection] = useState<MimeRejection | null>(
    null
  );
  const [isSkippingFile, setIsSkippingFile] = useState<boolean>(false);

  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const isErrorApiExceptionRef = useRef<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    UploadStatus.idle
  );
  // Mirror of uploadStatus so the beforeunload handler can read the latest
  // value without re-binding the listener on every transition.
  const uploadStatusRef = useRef<UploadStatus>(uploadStatus);
  uploadStatusRef.current = uploadStatus;

  const { isMobileWidth } = useWindowDimensions();
  const { requestScreenWakeLock, releaseScreenWakeLock, isWakeLockActive } =
    useWakeLock();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Only warn while bytes are actually in flight. Once the upload has
      // succeeded, failed, or been aborted the dialog stays open until the
      // user dismisses it, but there is nothing left to lose.
      if (uploadStatusRef.current !== UploadStatus.ongoing) return;

      event.preventDefault();
      // `returnValue` is deprecated but still required by legacy browsers.
      event.returnValue = true;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const onSendButtonPressed = async () => {
    // whether dynamic form has no error
    let isDynamicFormOk = true;

    if (!!validateDynamicForm) {
      isDynamicFormOk = await validateDynamicForm();
    }

    const postValidationErrors: Set<TransferDraftErrors> =
      validateFormAndReturnErrorIfAny(transferDraft);

    const hasDraftErrors =
      postValidationErrors.size > 0 || transferDraft.errors.size > 0;

    if (hasDraftErrors) {
      transferDraftDispatch({
        type: TransferDraftActionType.errorUpdate,
        postValidationErrors: postValidationErrors,
      });
    }

    if (!isDynamicFormOk || hasDraftErrors) {
      onValidationFailed?.();
      return;
    }

    setIsLoading(true);
    setAbortState(TransferAbortionState.none);
    setMimeRejection(null);
    setUploadStatus(UploadStatus.ongoing);

    if (!isWakeLockActive) await requestScreenWakeLock();

    const abortController = new AbortController();
    uploadAbortControllerRef.current = abortController;

    await runUpload(abortController.signal);
  };

  const runUpload = async (signal: AbortSignal, resume?: ResumeContext) => {
    try {
      setMimeRejection(null);
      setIsSkippingFile(false);

      await uploadMailboxTransfer(
        transferDraft,
        setProgress,
        { signal },
        vanityLink,
        resume
      );

      setUploadStatus(UploadStatus.success);

      const hasMailboxPrefill = !!transferDraft.prefillKeys;

      if (hasMailboxPrefill) {
        transferDraftDispatch({
          type: TransferDraftActionType.resetKeepPrefill,
          environment,
        });
      } else {
        transferDraftDispatch({
          type: TransferDraftActionType.reset,
          environment,
        });
      }
    } catch (error) {
      if (error instanceof UploadAbortedError) {
        setProgress(0);
        setAbortState(TransferAbortionState.aborted);
        setUploadStatus(UploadStatus.aborted);
        return;
      }

      if (error instanceof MimeTypeRejectedError) {
        const rejectedFileId = error.clientId;

        const remaining = transferDraft.files.filter(
          ([skpFile]) => skpFile.id !== rejectedFileId
        );

        setMimeRejection({
          fileName: error.fileName,
          rejectedFileId,
          reservationUid: error.reservationUid,
          reservationToken: error.reservationToken,
          remainingFiles: remaining,
          completedBytes: error.completedBytes,
          totalBytes: error.totalBytes,
        });
        return;
      }

      let userFacingError: Error = new Error();
      userFacingError.name = t(`File upload failed`);

      setUploadStatus(UploadStatus.failed);

      if (!(error instanceof AxiosError)) {
        userFacingError.message = (error as Error).message;
        setError(userFacingError);
        return;
      }

      if (error.code === AxiosError.ERR_CANCELED) {
        setIsLoading(false);
        setProgress(0);
        setAbortState(TransferAbortionState.none);
        return;
      }

      const response: AxiosResponse<unknown, any> | undefined = error.response;

      if (!response) {
        const isUserOffline: boolean = error.code === AxiosError.ERR_NETWORK;

        userFacingError.message = isUserOffline
          ? t(
              `It seems like you have no access to the internet right now. Please make sure that your internet connection is stable and then try again.`
            )
          : (error as AxiosError).message;

        setError(userFacingError);
        return;
      }

      isErrorApiExceptionRef.current = isApiException(response.data);

      if (isErrorApiExceptionRef.current) {
        const apiException: ApiException = response.data as ApiException;

        userFacingError.message = getErrorMessage(apiException, t);
      } else {
        const isAxiosResponseDataString: boolean =
          (response as AxiosResponse<unknown, any>).data instanceof String;

        if (isAxiosResponseDataString) {
          userFacingError.message = (response as AxiosResponse<unknown, any>)
            .data as string;
        } else {
          userFacingError.message = t(
            "Something went wrong. If refreshing the page does not fix the problem please contact our support team."
          );
        }
      }

      setError(userFacingError);
    } finally {
      uploadAbortControllerRef.current = null;
      if (isWakeLockActive) await releaseScreenWakeLock();
    }
  };

  const handleSkipFile = async () => {
    if (!mimeRejection || isSkippingFile) return;

    setIsSkippingFile(true);

    try {
      const {
        remainingFiles,
        reservationUid,
        reservationToken,
        completedBytes,
      } = mimeRejection;

      const fileToRemove = transferDraft.files.find(
        ([skpFile]) => skpFile.id === mimeRejection.rejectedFileId
      );
      if (fileToRemove) {
        transferDraftDispatch({
          type: TransferDraftActionType.filesRemove,
          removedFiles: [fileToRemove],
        });
      }

      if (remainingFiles.length === 0) {
        await cancelReservation(reservationUid);
        setMimeRejection(null);
        setIsLoading(false);
        setProgress(0);
        setUploadStatus(UploadStatus.idle);
        setIsSkippingFile(false);
        return;
      }

      const newTotalBytes =
        remainingFiles.reduce((acc, [skpFile]) => acc + skpFile.size, 0) +
        completedBytes;

      const abortController = new AbortController();
      uploadAbortControllerRef.current = abortController;

      const resumeCtx: ResumeContext = {
        reservationUid,
        reservationToken,
        files: remainingFiles,
        completedBytes,
        totalBytes: newTotalBytes,
      };

      await runUpload(abortController.signal, resumeCtx);
    } catch {
      setIsSkippingFile(false);
      setUploadStatus(UploadStatus.failed);
      setMimeRejection(null);
    }
  };

  const handleReturnToDraft = async () => {
    if (!mimeRejection) return;

    try {
      await cancelReservation(mimeRejection.reservationUid);
    } catch {
      // best-effort cancellation
    }

    setMimeRejection(null);
    setIsLoading(false);
    setProgress(0);
    setAbortState(TransferAbortionState.none);
    setUploadStatus(UploadStatus.idle);
  };

  const handleAbortUpload = async () => {
    if (
      abortState === TransferAbortionState.aborting ||
      abortState === TransferAbortionState.aborted
    ) {
      return;
    }
    setAbortState(TransferAbortionState.aborting);
    uploadAbortControllerRef.current?.abort();
  };

  return (
    <>
      <Button
        type="button"
        variant="contained"
        fullWidth={isMobileWidth}
        onClick={() => {
          onSendButtonPressed();
        }}
      >
        <div className="font-ibm-plex-sans font-medium">
          {isLoading ? <Spinner color="white" size={16} /> : t(`Send message`)}
        </div>
      </Button>
      {isLoading && (
        <UploadStatusProvider uploadStatus={uploadStatus}>
          <UploadDialogThemeProvider>
            <UploadDialog
              error={error}
              progress={progress}
              draft={transferDraft}
              onDismiss={() => {
                setIsLoading(false);
                setAbortState(TransferAbortionState.none);
                setMimeRejection(null);
                setUploadStatus(UploadStatus.idle);
              }}
              onConfirmAbort={handleAbortUpload}
              abortState={abortState}
              mimeRejection={
                mimeRejection
                  ? {
                      fileName: mimeRejection.fileName,
                      isSkipping: isSkippingFile,
                      onSkipFile: handleSkipFile,
                      onReturnToDraft: handleReturnToDraft,
                    }
                  : null
              }
            />
          </UploadDialogThemeProvider>
        </UploadStatusProvider>
      )}
      {error && (
        <ExceptionDialog
          title={error.name}
          description={error.message}
          isApiException={isErrorApiExceptionRef.current}
          onDismiss={() => {
            setIsLoading(false);
            setError(null);
          }}
        />
      )}
    </>
  );
}
